import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
} from "react";
import {
  AdminApiError,
  createAdminDepartment,
  type CompanyResponse,
  type DepartmentCreatePayload,
} from "../../api/admin";
import "./DepartmentCreatePanel.css";

export type DepartmentCreatePanelProps = {
  companies: ReadonlyArray<CompanyResponse>;
  disabled: boolean;
  onCreated: () => Promise<void>;
  onCreateOpen?: () => void;
};

type FieldErrors = {
  company: string;
  name: string;
  code: string;
  sortOrder: string;
  routing: string;
};

type FormState = {
  companyId: number | null;
  name: string;
  code: string;
  sortOrder: string;
  isRoutingEnabled: boolean;
};

type UiState = {
  isOpen: boolean;
  isSubmitting: boolean;
  fieldErrors: FieldErrors;
  formError: string;
  success: string;
};

const INITIAL_FORM: FormState = {
  companyId: null,
  name: "",
  code: "",
  sortOrder: "0",
  isRoutingEnabled: false,
};

const INITIAL_UI: UiState = {
  isOpen: false,
  isSubmitting: false,
  fieldErrors: { company: "", name: "", code: "", sortOrder: "", routing: "" },
  formError: "",
  success: "",
};

function pickSoleCompanyId(companies: ReadonlyArray<CompanyResponse>): number | null {
  if (companies.length === 1) {
    return companies[0]?.id ?? null;
  }
  return null;
}

function resolveFieldNameFromLocation(
  location: ReadonlyArray<string | number>,
): string | null {
  for (let index = location.length - 1; index >= 0; index -= 1) {
    const segment = location[index];
    if (typeof segment === "string" && segment.length > 0) {
      return segment;
    }
  }
  return null;
}

const FRIENDLY_FIELD_ERROR_MESSAGES: Readonly<Record<string, string>> = {
  INVALID_CODE:
    "Code must start with a capital letter and contain only capital letters, numbers, or underscores.",
};

function friendlyFieldErrorMessage(errorCode: string | null, fallback: string): string {
  if (errorCode === null) {
    return fallback;
  }
  const mapped = FRIENDLY_FIELD_ERROR_MESSAGES[errorCode];
  return mapped ?? fallback;
}

export default function DepartmentCreatePanel(props: DepartmentCreatePanelProps) {
  const { companies, disabled, onCreated, onCreateOpen } = props;
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [ui, setUi] = useState<UiState>(INITIAL_UI);
  const headingId = useId();
  const companyErrorId = useId();
  const nameErrorId = useId();
  const codeErrorId = useId();
  const sortErrorId = useId();
  const routingErrorId = useId();
  const formErrorId = useId();
  const noCompanyNoticeId = useId();
  const addButtonRef = useRef<HTMLButtonElement | null>(null);
  const companySelectRef = useRef<HTMLSelectElement | null>(null);
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const isMountedRef = useRef(true);
  const shouldReturnFocusRef = useRef(false);
  const submitInFlightRef = useRef(false);

  const clearNotifications = useCallback(() => {
    setUi((prev) => ({
      ...prev,
      fieldErrors: { company: "", name: "", code: "", sortOrder: "", routing: "" },
      formError: "",
      success: "",
    }));
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const hasCompanies = companies.length > 0;
  const noCompanyAvailable = companies.length === 0;

  const openForm = useCallback(() => {
    if (disabled || ui.isSubmitting || !hasCompanies) {
      return;
    }
    onCreateOpen?.();
    const soleId = pickSoleCompanyId(companies);
    setForm({
      ...INITIAL_FORM,
      companyId: soleId,
    });
    setUi({
      ...INITIAL_UI,
      isOpen: true,
    });
    setTimeout(() => {
      if (companies.length > 1) {
        companySelectRef.current?.focus();
      } else {
        nameInputRef.current?.focus();
      }
    }, 0);
  }, [companies, disabled, hasCompanies, onCreateOpen, ui.isSubmitting]);

  const closeForm = useCallback(() => {
    setForm(INITIAL_FORM);
    setUi(INITIAL_UI);
    shouldReturnFocusRef.current = true;
  }, []);

  useEffect(() => {
    if (ui.isOpen || !shouldReturnFocusRef.current) {
      return;
    }

    if (noCompanyAvailable) {
      shouldReturnFocusRef.current = false;
      return;
    }

    if (disabled) {
      return;
    }

    shouldReturnFocusRef.current = false;
    addButtonRef.current?.focus();
  }, [disabled, noCompanyAvailable, ui.isOpen]);

  useEffect(() => {
    if (!ui.isOpen) {
      return;
    }
    setForm((prev) => {
      const soleId = pickSoleCompanyId(companies);
      if (soleId !== null) {
        return { ...prev, companyId: soleId };
      }
      if (
        prev.companyId !== null &&
        companies.some((company) => company.id === prev.companyId)
      ) {
        return prev;
      }
      return { ...prev, companyId: null };
    });
  }, [companies, ui.isOpen]);

  const handleCompanyChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const rawValue = event.target.value;
      const parsed = rawValue === "" ? null : Number.parseInt(rawValue, 10);
      const nextValue = parsed !== null && Number.isFinite(parsed) ? parsed : null;
      setForm((prev) => ({
        ...prev,
        companyId: nextValue,
      }));
      setUi((prev) => ({
        ...prev,
        fieldErrors: { ...prev.fieldErrors, company: "" },
        formError: "",
      }));
    },
    [],
  );

  const handleNameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, name: event.target.value }));
      setUi((prev) => ({
        ...prev,
        fieldErrors: { ...prev.fieldErrors, name: "" },
        formError: "",
      }));
    },
    [],
  );

  const handleCodeChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, code: event.target.value }));
      setUi((prev) => ({
        ...prev,
        fieldErrors: { ...prev.fieldErrors, code: "" },
        formError: "",
      }));
    },
    [],
  );

  const handleSortOrderChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, sortOrder: event.target.value }));
      setUi((prev) => ({
        ...prev,
        fieldErrors: { ...prev.fieldErrors, sortOrder: "" },
        formError: "",
      }));
    },
    [],
  );

  const handleRoutingToggle = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({
        ...prev,
        isRoutingEnabled: event.target.checked,
      }));
    },
    [],
  );

  const buildClientFieldErrors = useCallback((): FieldErrors => {
    const trimmedName = form.name.trim();
    const trimmedCode = form.code.trim();
    const trimmedSort = form.sortOrder.trim();
    const sortAsNumber =
      trimmedSort.length === 0 ? Number.NaN : Number(trimmedSort);
    const sortIsValidInteger =
      trimmedSort.length > 0 &&
      Number.isFinite(sortAsNumber) &&
      Number.isInteger(sortAsNumber);
    const fieldErrors: FieldErrors = {
      company:
        companies.length > 1 && form.companyId === null
          ? "Please choose a company."
          : "",
      name: trimmedName.length === 0 ? "Please provide a department name." : "",
      code: trimmedCode.length === 0 ? "Please provide a department code." : "",
      sortOrder: sortIsValidInteger
        ? ""
        : "Sort order must be a whole number.",
      routing: "",
    };
    return fieldErrors;
  }, [
    companies.length,
    form.companyId,
    form.code,
    form.name,
    form.sortOrder,
  ]);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (submitInFlightRef.current || disabled || noCompanyAvailable) {
        return;
      }
      clearNotifications();

      const clientErrors = buildClientFieldErrors();
      const hasClientErrors =
        clientErrors.company.length > 0 ||
        clientErrors.name.length > 0 ||
        clientErrors.code.length > 0 ||
        clientErrors.sortOrder.length > 0;
      if (hasClientErrors) {
        setUi((prev) => ({
          ...prev,
          fieldErrors: clientErrors,
        }));
        return;
      }

      if (form.companyId === null) {
        setUi((prev) => ({
          ...prev,
          fieldErrors: {
            ...prev.fieldErrors,
            company: "Please choose a company.",
          },
        }));
        return;
      }

      submitInFlightRef.current = true;
      setUi((prev) => ({ ...prev, isSubmitting: true }));

      const trimmedName = form.name.trim();
      const trimmedCode = form.code.trim();
      const sortAsNumber = Number(form.sortOrder.trim());

      const payload: DepartmentCreatePayload = {
        company_id: form.companyId,
        name: trimmedName,
        code: trimmedCode,
        sort_order: sortAsNumber,
        is_ticket_routing_enabled: form.isRoutingEnabled,
      };

      try {
        await createAdminDepartment(payload);
        if (!isMountedRef.current) {
          return;
        }
        setForm(INITIAL_FORM);
        setUi({
          ...INITIAL_UI,
          isOpen: false,
          success: "Department created.",
        });
        shouldReturnFocusRef.current = true;
        await onCreated();
      } catch (error) {
        if (!isMountedRef.current) {
          return;
        }
        if (error instanceof AdminApiError) {
          const fieldErrors: FieldErrors = {
            company: "",
            name: "",
            code: "",
            sortOrder: "",
            routing: "",
          };
          let hasRecognisedFieldError = false;
          let hasUnrecognisedFieldError = false;
          for (const fieldError of error.fieldErrors) {
            const fieldName = resolveFieldNameFromLocation(fieldError.location);
            if (fieldName === null) {
              hasUnrecognisedFieldError = true;
              continue;
            }
            if (fieldName === "company_id") {
              fieldErrors.company = fieldError.message;
              hasRecognisedFieldError = true;
            } else if (fieldName === "name") {
              fieldErrors.name = fieldError.message;
              hasRecognisedFieldError = true;
            } else if (fieldName === "code") {
              fieldErrors.code = friendlyFieldErrorMessage(
                error.errorCode,
                fieldError.message,
              );
              hasRecognisedFieldError = true;
            } else if (fieldName === "sort_order") {
              fieldErrors.sortOrder = fieldError.message;
              hasRecognisedFieldError = true;
            } else if (fieldName === "is_ticket_routing_enabled") {
              fieldErrors.routing = fieldError.message;
              hasRecognisedFieldError = true;
            } else {
              hasUnrecognisedFieldError = true;
            }
          }
          const showDetail =
            !hasRecognisedFieldError || hasUnrecognisedFieldError;
          setUi((prev) => ({
            ...prev,
            isSubmitting: false,
            fieldErrors,
            formError: showDetail
              ? friendlyFieldErrorMessage(error.errorCode, error.detail)
              : "",
          }));
          return;
        }
        setUi((prev) => ({
          ...prev,
          isSubmitting: false,
          formError: "Unable to create the department. Please try again.",
        }));
      } finally {
        if (isMountedRef.current) {
          submitInFlightRef.current = false;
        }
      }
    },
    [buildClientFieldErrors, clearNotifications, disabled, form, noCompanyAvailable, onCreated],
  );

  const soleCompany =
    companies.length === 1 ? companies[0] ?? null : null;
  const isMultiCompany = companies.length > 1;
  const allInputsDisabled = disabled || ui.isSubmitting || noCompanyAvailable;

  return (
    <div className="department-create-panel">
      {ui.success && !ui.isOpen ? (
        <p className="department-create-panel__success" aria-live="polite">
          {ui.success}
        </p>
      ) : null}

      {!ui.isOpen ? (
        <div className="department-create-panel__closed">
          <button
            type="button"
            ref={addButtonRef}
            className="department-create-panel__add"
            onClick={openForm}
            disabled={allInputsDisabled}
            aria-expanded={false}
            aria-describedby={
              noCompanyAvailable ? noCompanyNoticeId : undefined
            }
          >
            Add Department
          </button>
          {noCompanyAvailable ? (
            <p
              id={noCompanyNoticeId}
              className="department-create-panel__notice"
              role="status"
            >
              Department creation is unavailable because no active Internal
              company is configured.
            </p>
          ) : null}
        </div>
      ) : null}

      {ui.isOpen ? (
        <form
          className="department-create-panel__form"
          onSubmit={handleSubmit}
          noValidate
          aria-labelledby={headingId}
        >
          <h2 id={headingId} className="department-create-panel__heading">
            Add Department
          </h2>

          {ui.formError ? (
            <p
              id={formErrorId}
              className="department-create-panel__error"
              role="alert"
            >
              {ui.formError}
            </p>
          ) : null}

          {isMultiCompany ? (
            <div className="department-create-panel__field">
              <label
                htmlFor="department-create-company"
                className="department-create-panel__label"
              >
                Company
              </label>
              <select
                id="department-create-company"
                ref={companySelectRef}
                className="department-create-panel__select"
                value={form.companyId === null ? "" : String(form.companyId)}
                onChange={handleCompanyChange}
                disabled={allInputsDisabled}
                required
                aria-required="true"
                aria-invalid={ui.fieldErrors.company.length > 0}
                aria-describedby={
                  ui.fieldErrors.company.length > 0 ? companyErrorId : undefined
                }
              >
                <option value="" disabled>
                  Select a company…
                </option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
              {ui.fieldErrors.company.length > 0 ? (
                <p
                  id={companyErrorId}
                  className="department-create-panel__field-error"
                >
                  {ui.fieldErrors.company}
                </p>
              ) : null}
            </div>
          ) : soleCompany !== null ? (
            <div className="department-create-panel__company-readonly">
              <p className="department-create-panel__company-readonly-line">
                Company: <strong>{soleCompany.name}</strong>
              </p>
              {ui.fieldErrors.company.length > 0 ? (
                <p
                  id={companyErrorId}
                  className="department-create-panel__field-error"
                >
                  {ui.fieldErrors.company}
                </p>
              ) : null}
            </div>
          ) : (
            <p
              id={noCompanyNoticeId}
              className="department-create-panel__notice"
              role="status"
            >
              Department creation is unavailable because no active Internal
              company is configured.
            </p>
          )}

          <div className="department-create-panel__field">
            <label
              htmlFor="department-create-name"
              className="department-create-panel__label"
            >
              Department name
            </label>
            <input
              id="department-create-name"
              ref={nameInputRef}
              className="department-create-panel__input"
              type="text"
              value={form.name}
              onChange={handleNameChange}
              disabled={allInputsDisabled}
              required
              aria-required="true"
              aria-invalid={ui.fieldErrors.name.length > 0}
              aria-describedby={
                ui.fieldErrors.name.length > 0 ? nameErrorId : undefined
              }
            />
            {ui.fieldErrors.name.length > 0 ? (
              <p
                id={nameErrorId}
                className="department-create-panel__field-error"
              >
                {ui.fieldErrors.name}
              </p>
            ) : null}
          </div>

          <div className="department-create-panel__field">
            <label
              htmlFor="department-create-code"
              className="department-create-panel__label"
            >
              Department code
            </label>
            <input
              id="department-create-code"
              className="department-create-panel__input"
              type="text"
              value={form.code}
              onChange={handleCodeChange}
              disabled={allInputsDisabled}
              required
              aria-required="true"
              aria-invalid={ui.fieldErrors.code.length > 0}
              aria-describedby={
                ui.fieldErrors.code.length > 0 ? codeErrorId : undefined
              }
            />
            {ui.fieldErrors.code.length > 0 ? (
              <p
                id={codeErrorId}
                className="department-create-panel__field-error"
              >
                {ui.fieldErrors.code}
              </p>
            ) : null}
          </div>

          <div className="department-create-panel__field">
            <label
              htmlFor="department-create-sort-order"
              className="department-create-panel__label"
            >
              Sort order
            </label>
            <input
              id="department-create-sort-order"
              className="department-create-panel__input"
              type="number"
              inputMode="numeric"
              step={1}
              value={form.sortOrder}
              onChange={handleSortOrderChange}
              disabled={allInputsDisabled}
              required
              aria-required="true"
              aria-invalid={ui.fieldErrors.sortOrder.length > 0}
              aria-describedby={
                ui.fieldErrors.sortOrder.length > 0 ? sortErrorId : undefined
              }
            />
            {ui.fieldErrors.sortOrder.length > 0 ? (
              <p
                id={sortErrorId}
                className="department-create-panel__field-error"
              >
                {ui.fieldErrors.sortOrder}
              </p>
            ) : null}
          </div>

          <div className="department-create-panel__field department-create-panel__field--inline">
            <label
              htmlFor="department-create-routing"
              className="department-create-panel__label department-create-panel__label--inline"
            >
              <input
                id="department-create-routing"
                type="checkbox"
                checked={form.isRoutingEnabled}
                onChange={handleRoutingToggle}
                disabled={allInputsDisabled}
                aria-invalid={ui.fieldErrors.routing.length > 0}
                aria-describedby={
                  ui.fieldErrors.routing.length > 0 ? routingErrorId : undefined
                }
              />
              <span>Ticket routing enabled</span>
            </label>
            {ui.fieldErrors.routing.length > 0 ? (
              <p
                id={routingErrorId}
                className="department-create-panel__field-error"
              >
                {ui.fieldErrors.routing}
              </p>
            ) : null}
          </div>

          <div
            className="department-create-panel__actions"
            role="group"
            aria-labelledby={headingId}
          >
            <button
              type="submit"
              className="department-create-panel__submit"
              disabled={allInputsDisabled}
              aria-busy={ui.isSubmitting}
            >
              {ui.isSubmitting ? "Creating…" : "Create Department"}
            </button>
            <button
              type="button"
              className="department-create-panel__cancel"
              onClick={closeForm}
              disabled={ui.isSubmitting}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
